import { Download, File, TriangleAlert, User, X } from "lucide-react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/Button";
import { ErrorState } from "@/components/ErrorState";
import { LoadingState } from "@/components/LoadingState";
import { Modal, ModalTitle } from "@/components/Modal";
import { useApi } from "@/lib/api";
import { formatDateTime } from "@/lib/formatDateTime";
import {
  type AiAttachmentSummary,
  type AiConversationDetail,
  type AiConversationMessages,
  type AiConversationSummary,
  attachmentKind,
  displayTitle,
  groupMessages,
  type MessagePart,
  messageParts,
  sourceLabel,
  type RecordedMessage,
} from "./aiShared";
import { formatSize } from "./shared";

export function AiRecordingViewer({
  orgId,
  recording,
  onClose,
}: {
  orgId: string;
  recording: AiConversationSummary;
  onClose: () => void;
}) {
  const api = useApi();
  const basePath = `/organizations/${orgId}/ai-conversations/${recording.id}`;

  const [detail, setDetail] = useState<AiConversationDetail | null>(null);
  const [messages, setMessages] = useState<RecordedMessage[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [loadingChunk, setLoadingChunk] = useState(false);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const applyMessages = useCallback((data: AiConversationMessages) => {
    setMessages((current) => [...current, ...data.messages]);
    setNext(
      data.nextCursor ? `?cursor=${encodeURIComponent(data.nextCursor)}` : null,
    );

    if (data.unavailable) setUnavailable(data.unavailable);
  }, []);

  useEffect(() => {
    let cancelled = false;

    void Promise.all([
      api.get<AiConversationDetail>(basePath),
      api.get<AiConversationMessages>(`${basePath}/messages`),
    ]).then(([detailData, firstChunk]) => {
      if (cancelled) return;

      if (!detailData || !firstChunk) {
        setError("Couldn't load the recording.");

        return;
      }

      setDetail(detailData);
      applyMessages(firstChunk);
    });

    return () => {
      cancelled = true;
    };
  }, [api, applyMessages, basePath]);

  const loadNextChunk = useCallback(async () => {
    if (!detail || next === null || loadingChunk) return;

    setLoadingChunk(true);

    const data = await api.get<AiConversationMessages>(
      `${basePath}/messages${next}`,
    );

    setLoadingChunk(false);

    if (data) {
      applyMessages(data);
    } else {
      setError("Couldn't load the messages.");
    }
  }, [api, applyMessages, basePath, detail, loadingChunk, next]);

  useEffect(() => {
    const sentinel = sentinelRef.current;

    if (
      !sentinel ||
      next === null ||
      typeof IntersectionObserver === "undefined"
    ) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void loadNextChunk();
    });

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [loadNextChunk, next]);

  async function download(attachment: AiAttachmentSummary) {
    setDownloadError(null);

    const content = await api.getBinary(
      `${basePath}/attachments/${attachment.id}`,
    );

    if (!content) {
      setDownloadError(`Couldn't download ${attachment.filename}.`);

      return;
    }

    const url = URL.createObjectURL(
      new Blob([content], { type: "application/octet-stream" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = attachment.filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  const attachmentsByMessage = new Map<string, AiAttachmentSummary[]>();

  for (const attachment of detail?.attachments ?? []) {
    const list = attachmentsByMessage.get(attachment.externalMessageId) ?? [];
    list.push(attachment);
    attachmentsByMessage.set(attachment.externalMessageId, list);
  }

  const who = recording.tendraUserName ?? recording.email ?? "Unknown user";

  return (
    <Modal onClose={onClose} maxWidth="max-w-5xl" scrollable={false}>
      <header className="relative mb-4 flex shrink-0 flex-col items-center gap-1.5 px-10 text-center">
        <UserMark recording={recording} />
        <ModalTitle className="text-[16px]">
          {displayTitle(recording)}
        </ModalTitle>
        <p className="text-xs text-foreground/70">
          {[
            who,
            recording.tendraUserName ? recording.email : null,
            sourceLabel(recording.source),
            recording.model,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-0 top-0 flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-foreground hover:bg-muted/80"
        >
          <X className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto rounded-xl bg-base px-4 py-4">
        {error ? (
          <ErrorState message={error} />
        ) : !detail ? (
          <LoadingState />
        ) : (
          <div className="flex flex-col gap-2.5">
            {recording.deletedAt && (
              <Notice>
                Deleted by the user on {formatDateTime(recording.deletedAt)}.
              </Notice>
            )}

            {unavailable && <Notice>{unavailable}</Notice>}

            {recording.startedBeforeRecording && (
              <p className="my-1 flex justify-center">
                <span className="inline-flex items-center gap-2 rounded-full border border-dashed border-muted px-3 py-1 text-xs text-foreground/70">
                  <span
                    aria-hidden="true"
                    className="h-[7px] w-[7px] rounded-full bg-success"
                  />
                  Recording started partway through this chat
                </span>
              </p>
            )}

            {groupMessages(messages, recording.source).map((group) => (
              <div key={group.key} className="flex flex-col gap-2.5">
                {group.showTime && group.startsAt && (
                  <p className="mt-2 text-center text-xs tabular-nums text-foreground/70">
                    {formatDateTime(group.startsAt)}
                  </p>
                )}

                <div
                  className={`flex items-end gap-2 ${group.fromUser ? "flex-row-reverse" : ""}`}
                >
                  {group.fromUser ? (
                    <UserMark recording={recording} />
                  ) : (
                    <ClaudeMark />
                  )}

                  <div
                    className={`flex min-w-0 flex-1 flex-col gap-1 ${group.fromUser ? "items-end" : "items-start"}`}
                  >
                    {group.messages.map((message, index) => (
                      <div
                        key={message.id}
                        className={`flex max-w-[78%] flex-col gap-1 ${group.fromUser ? "items-end" : "items-start"}`}
                      >
                        {(attachmentsByMessage.get(message.id) ?? []).map(
                          (attachment) => (
                            <FileCard
                              key={attachment.id}
                              attachment={attachment}
                              onDownload={() => void download(attachment)}
                            />
                          ),
                        )}
                        {messageParts(message, recording.source).map(
                          (part, partIndex) =>
                            part.kind === "text" ? (
                              <Bubble
                                key={partIndex}
                                fromUser={group.fromUser}
                                first={index === 0 && partIndex === 0}
                                last={index === group.messages.length - 1}
                                text={part.text}
                              />
                            ) : (
                              <ToolCard key={partIndex} part={part} />
                            ),
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}

            {!loadingChunk &&
              next === null &&
              messages.length === 0 &&
              !unavailable && (
                <p className="py-6 text-center text-sm text-foreground/70">
                  There are no recorded messages in this chat.
                </p>
              )}

            <div ref={sentinelRef} />

            {next !== null && messages.length > 0 && (
              <div className="flex justify-center py-2">
                <Button
                  size="sm"
                  onClick={() => void loadNextChunk()}
                  disabled={loadingChunk}
                >
                  {loadingChunk ? "Loading..." : "Load more"}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {downloadError && (
        <p
          role="alert"
          className="mt-3 shrink-0 text-center text-sm text-danger"
        >
          {downloadError}
        </p>
      )}
    </Modal>
  );
}

function Bubble({
  fromUser,
  first,
  last,
  text,
}: {
  fromUser: boolean;
  first: boolean;
  last: boolean;
  text: string;
}) {
  const joined = fromUser
    ? `${first ? "" : "rounded-tr-md"} ${last ? "" : "rounded-br-md"}`
    : `${first ? "" : "rounded-tl-md"} ${last ? "" : "rounded-bl-md"}`;

  return (
    <div
      className={`whitespace-pre-wrap wrap-anywhere rounded-[20px] px-3.5 py-2.5 text-sm leading-relaxed ${
        fromUser ? "bg-accent text-surface" : "bg-muted text-foreground"
      } ${joined}`}
    >
      {text}
    </div>
  );
}

function ToolCard({ part }: { part: Extract<MessagePart, { kind: "tool" }> }) {
  return (
    <details className="w-full max-w-full rounded-xl border border-muted bg-surface text-xs">
      <summary
        className={`cursor-pointer select-none px-3 py-2 font-medium ${part.isError ? "text-danger" : "text-foreground/70"}`}
      >
        {part.label}
      </summary>
      {part.detail && (
        <pre className="max-h-64 overflow-auto whitespace-pre-wrap wrap-anywhere border-t border-muted px-3 py-2 font-mono text-foreground/80">
          {part.detail}
        </pre>
      )}
    </details>
  );
}

function FileCard({
  attachment,
  onDownload,
}: {
  attachment: AiAttachmentSummary;
  onDownload: () => void;
}) {
  return (
    <div className="flex w-80 max-w-full items-center gap-3 rounded-2xl border border-muted bg-base px-3 py-2.5 text-left">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface text-foreground/70">
        <File className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-sm font-medium text-foreground">
          {attachment.filename}
        </span>
        <span className="text-xs text-foreground/70">
          {attachmentKind(attachment)}
          {attachment.sizeBytes !== null
            ? ` · ${formatSize(attachment.sizeBytes)}`
            : ""}
        </span>
      </span>
      <button
        type="button"
        onClick={onDownload}
        aria-label={`Download ${attachment.filename}`}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground hover:bg-muted/80 disabled:opacity-50"
      >
        <Download className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
      </button>
    </div>
  );
}

function Notice({ children }: { children: ReactNode }) {
  return (
    <div
      role="note"
      className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-foreground"
    >
      <TriangleAlert
        className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning"
        strokeWidth={3}
        aria-hidden="true"
      />
      <span>{children}</span>
    </div>
  );
}

export function UserMark({ recording }: { recording: AiConversationSummary }) {
  if (recording.tendraUserId) {
    return (
      <Avatar
        name={recording.tendraUserName}
        email={recording.email}
        avatarId={recording.tendraUserAvatarId}
        size="md"
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-dashed border-muted text-foreground/70"
    >
      <User className="h-3.5 w-3.5" strokeWidth={2} />
    </span>
  );
}

function ClaudeMark() {
  return (
    <span
      aria-hidden="true"
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-muted bg-surface text-xs font-bold text-accent"
    >
      C
    </span>
  );
}
